import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { CaseClientRow, ClientListItem } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useClientOptions } from '../../hooks/useLookups'
import { api } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { EmptyState, ErrorAlert } from '../common/Feedback'

function AddClientDialog({
  open,
  caseId,
  existingIds,
  onClose,
  onAdded,
}: {
  open: boolean
  caseId: string
  existingIds: string[]
  onClose: () => void
  onAdded: () => void
}) {
  const clients = useClientOptions()
  const [selected, setSelected] = useState<ClientListItem | null>(null)
  const [companyId, setCompanyId] = useState('')

  const options = (clients.data ?? []).filter((client) => !existingIds.includes(client.id))

  const add = useMutation({
    mutationFn: () =>
      api.post(`/cases/${caseId}/clients`, { clientId: selected?.id, companyId }),
    onSuccess: () => {
      setSelected(null)
      setCompanyId('')
      onAdded()
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>הוספת לקוח לתיק</DialogTitle>
      <DialogContent dividers>
        {add.error ? <ErrorAlert error={add.error} /> : null}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Autocomplete
            options={options}
            value={selected}
            loading={clients.isPending}
            getOptionLabel={(option) => option.displayName}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_event, option) => {
              setSelected(option)
              setCompanyId('')
            }}
            noOptionsText="אין לקוחות פנויים"
            renderInput={(params) => <TextField {...params} label="לקוח" autoFocus />}
          />
          <Typography variant="caption" color="text.secondary">
            החברה נבחרת אחרי הוספת הלקוח, מתוך החברות שבבעלותו.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ביטול</Button>
        <Button
          variant="contained"
          disabled={!selected || add.isPending}
          onClick={() => add.mutate()}
        >
          הוספה
        </Button>
      </DialogActions>
    </Dialog>
  )
}

type Props = {
  caseId: string
  clients: CaseClientRow[]
}

/**
 * Clients in a case. Each one acts personally or through a company they own — the
 * picker only offers that client's own companies (spec §3.6).
 */
export function CaseClientsPanel({ caseId, clients }: Props) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [addOpen, setAddOpen] = useState(false)
  const [pendingRemove, setPendingRemove] = useState<CaseClientRow | null>(null)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: qk.case(caseId) })
    void queryClient.invalidateQueries({ queryKey: ['cases'] })
    void queryClient.invalidateQueries({ queryKey: ['case-documents'] })
  }

  const setCompany = useMutation({
    mutationFn: (vars: { clientId: string; companyId: string }) =>
      api.patch(`/cases/${caseId}/clients/${vars.clientId}`, { companyId: vars.companyId }),
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: (clientId: string) => api.del(`/cases/${caseId}/clients/${clientId}`),
    onSuccess: () => {
      setPendingRemove(null)
      refresh()
    },
  })

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          הוספת לקוח
        </Button>
      </Stack>

      {setCompany.error ? <ErrorAlert error={setCompany.error} /> : null}
      {remove.error ? <ErrorAlert error={remove.error} /> : null}

      {clients.length === 0 ? (
        <EmptyState title="אין לקוחות בתיק" hint="יש להוסיף לפחות לקוח אחד" />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>לקוח</TableCell>
              <TableCell sx={{ width: 280 }}>פועל דרך חברה</TableCell>
              <TableCell align="left" />
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => navigate(`/clients/${row.clientId}`)}
                    sx={{ font: 'inherit' }}
                  >
                    {row.clientName}
                  </Link>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={row.companyId ?? ''}
                    disabled={setCompany.isPending}
                    onChange={(e) =>
                      setCompany.mutate({ clientId: row.clientId, companyId: e.target.value })
                    }
                    helperText={
                      row.availableCompanies.length === 0 ? 'ללקוח אין חברות' : undefined
                    }
                  >
                    <MenuItem value="">באופן אישי</MenuItem>
                    {row.availableCompanies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.displayName}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="left">
                  <IconButton size="small" onClick={() => setPendingRemove(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddClientDialog
        open={addOpen}
        caseId={caseId}
        existingIds={clients.map((row) => row.clientId)}
        onClose={() => setAddOpen(false)}
        onAdded={refresh}
      />

      <ConfirmDialog
        open={Boolean(pendingRemove)}
        title="הסרת לקוח מהתיק"
        message={`${pendingRemove?.clientName ?? ''} יוסר מהתיק. כרטיס הלקוח עצמו יישאר במערכת.`}
        confirmLabel="הסרה"
        busy={remove.isPending}
        onConfirm={() => pendingRemove && remove.mutate(pendingRemove.clientId)}
        onClose={() => setPendingRemove(null)}
      />
    </>
  )
}
